import React, { ComponentPropsWithoutRef, useMemo, useRef } from 'react';
import { DefaultBsComponent } from '../../types/module';
import { classNames } from '../../utils/style';
import { applyColor, applyVariant } from './buttonClassNames';

import { useObfuscation } from '../../utils/obfuscation';
import { fontActionMini, fontBodyMedium } from '../../styles/typographyNew';

export type ButtonV2Props = (
  | ComponentPropsWithoutRef<'button'>
  | ComponentPropsWithoutRef<'a'>
) &
  DefaultBsComponent & {
    variant?: 'primary' | 'minimal' | 'secondary';
    title?: any;
    color?: 'default' | 'invert';
    isDisabled?: boolean;
    isObfuscated?: boolean;
    onClick?: any;
    activeHoverInCard?: boolean;
    navigate?: (url: string) => void;
    inline?: boolean;
    __typename?: string;
  };

const useButtonProps = ({
  isObfuscated,
  Component,
  href,
  ref,
  navigate,
  isDisabled,
  variant,
  activeHoverInCard,
  inline,
}: any) => {
  useObfuscation(ref, isObfuscated ? href : undefined, navigate);

  const ComponentBtn = isObfuscated && Component === 'a' ? 'span' : Component;

  const displayHref = useMemo(() => {
    if (href && ((isObfuscated && Component === 'a') || Component !== 'a')) {
      return null;
    }
    return href;
  }, [href, Component, isObfuscated]);

  return {
    ComponentBtn,
    displayHref,
    classNamesBtn: classNames(
      'group',
      !isDisabled ? 'cursor-pointer' : 'cursor-default',
      applyColor(isDisabled, variant, activeHoverInCard),
      applyVariant(variant),
      'whitespace-nowrap',
      !inline && variant !== 'minimal'
        ? 'flex items-center justify-center'
        : 'inline-block',
    ),
  };
};

export const ButtonV2 = ({
  as: Component = 'button',
  children,
  variant = 'primary',
  title,
  isDisabled = false,
  isObfuscated = false,
  className,
  onClick,
  activeHoverInCard,
  navigate,
  inline,
  __typename,
  ...props
}: ButtonV2Props) => {
  const ref = useRef<HTMLElement>(null);
  const { ComponentBtn, displayHref, classNamesBtn } = useButtonProps({
    isObfuscated,
    Component,
    href: props?.href,
    ref,
    navigate,
    isDisabled,
    variant,
    activeHoverInCard,
    inline,
  });

  return (
    <ComponentBtn
      ref={ref}
      onClick={(event: any) => {
        if (!isDisabled && onClick) {
          onClick(event);
        }
      }}
      href={displayHref}
      {...props}
      className={classNames(
        'ButtonV2',
        variant !== 'minimal' && 'py-12 px-16 min-h-[51px]',
        classNamesBtn,
        fontBodyMedium,
        className,
      )}
    >
      {children || title}
    </ComponentBtn>
  );
};

export const ButtonNavbar = ({
  as: Component = 'button',
  children,
  variant = 'primary',
  title,
  isDisabled = false,
  isObfuscated = false,
  className,
  onClick,
  activeHoverInCard,
  inline,
  navigate,
  ...props
}: ButtonV2Props) => {
  const ref = useRef<HTMLElement>(null);
  const { ComponentBtn, displayHref, classNamesBtn } = useButtonProps({
    isObfuscated,
    Component,
    href: props?.href,
    ref,
    navigate,
    isDisabled,
    variant,
    inline,
    activeHoverInCard,
  });

  return (
    <ComponentBtn
      ref={ref}
      onClick={(onClick && !isDisabled && onClick) || undefined}
      href={displayHref}
      {...props}
      className={classNames(
        'ButtonNavbar',
        fontActionMini,
        classNamesBtn,
        'py-8 px-12 lg:py-12 lg:px-16',
        'min-h-40 lg:min-h-48',
        className,
      )}
    >
      {children || title}
    </ComponentBtn>
  );
};
